import { Lock, LockOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/** Managers-only content is visible to admins only. */
export function useCanManageAdminOnly() {
  const { role } = useAuth();
  return role === "admin";
}

export function ManagersOnlyBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-normal text-amber-700 dark:text-amber-400",
        className,
      )}
      data-testid="managers-only-badge"
    >
      <Lock className="h-3 w-3" />
      Managers only
    </Badge>
  );
}

/**
 * Toggle between "visible to everyone" and "managers only". Renders nothing
 * for non-admins, who never get managers-only rows in the first place.
 */
export function ManagersOnlyToggle({
  adminOnly,
  onChange,
  disabled,
  className,
}: {
  adminOnly: boolean;
  onChange: (adminOnly: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const canManage = useCanManageAdminOnly();
  if (!canManage) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "h-7",
            adminOnly &&
              "border-amber-500/60 text-amber-700 dark:text-amber-400",
            className,
          )}
          disabled={disabled}
          aria-pressed={adminOnly}
          onClick={() => onChange(!adminOnly)}
          data-testid="managers-only-toggle"
        >
          {adminOnly ? (
            <Lock className="h-3 w-3 mr-1" />
          ) : (
            <LockOpen className="h-3 w-3 mr-1" />
          )}
          {adminOnly ? "Managers only" : "Everyone"}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {adminOnly
          ? "Only managers can see this. Click to share with everyone."
          : "Visible to all employees. Click to restrict to managers."}
      </TooltipContent>
    </Tooltip>
  );
}
